import { API, LOGIN_API, METRICS_API } from "../../constants";
import { ApiResponse } from "../../types";
import { getClientSecretBasicAuthHeader } from "../../utils";
import { apiSlice } from "../api-slice";
import {
  GetUserByTokenQueryParams,
  LogoutQueryParams,
  SupplementalDetails,
  User,
  UserPlaySettings,
  UserTokenResponse,
} from "./types";

const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /** ======== Get User By Token ======== */
    getUserByToken: build.query<User, GetUserByTokenQueryParams>({
      query: ({ token }) => ({
        method: "GET",
        url: `${LOGIN_API}/token/${token}`,
        headers: getClientSecretBasicAuthHeader(),
      }),
      transformResponse: (user: UserTokenResponse) => {
        const fastDrawAccount = user.products.FASTDRAW2?.[0]?.account;
        const fastDrawAccountProperties = fastDrawAccount?.properties;
        const isConsumerProperty = fastDrawAccountProperties?.find((property) => property.key === "isConsumer");
        const hideFdCourtBrandingProperty = fastDrawAccountProperties?.find(
          (property) => property.key === "hide_fd_court_branding"
        );

        // create supplemental user details
        const supplementalUserDetails: SupplementalDetails = {
          AdvancedPlaybooks: user.scopes.includes("advanced_playbooks"),
          AccountId: fastDrawAccount?.id || -1,
          leagueId: fastDrawAccountProperties?.find((property) => property.key === "league_id")?.value || "",
          AccountType: isConsumerProperty && isConsumerProperty.value === "true" ? "consumer" : "enterprise",
          CourtLogo: hideFdCourtBrandingProperty !== undefined && hideFdCourtBrandingProperty.value === "true",
          teamId: fastDrawAccountProperties?.find((property) => property.key === "team_id")?.value || "",
        };

        return { ...user, userDetails: { ...user.userDetails, ...supplementalUserDetails } };
      },
    }),

    /** ======== Post User Metrics ======== */
    getUserPlaySettings: build.query<UserPlaySettings, void>({
      query: () => `${API}/playsettings`,
      transformResponse: (response: ApiResponse<UserPlaySettings>) => response.data,
      providesTags: ["UserPlaySettings"],
    }),

    updateUserPlaySettings: build.mutation<UserPlaySettings, UserPlaySettings>({
      query: (userPlaySettings) => ({
        method: "POST",
        url: `${API}/playsettings`,
        body: userPlaySettings,
      }),
      transformResponse: (response: ApiResponse<UserPlaySettings>) => response.data,
      invalidatesTags: ["UserPlaySettings"],
    }),

    /** ======== Post User Metrics ======== */
    postUserMetrics: build.mutation<Object, void>({
      query: () => ({ method: "POST", url: `${METRICS_API}`, body: {} }),
    }),

    /** ======== Delete Cookie ======== */
    deleteUserCookie: build.mutation<void, void>({
      query: () => ({
        method: "DELETE",
        url: `${LOGIN_API}/cookie`,
        headers: { ...getClientSecretBasicAuthHeader() },
        credentials: "include",
      }),
    }),

    /** ======== Logout ======== */
    logout: build.mutation<Object, LogoutQueryParams>({
      query: ({ token }) => ({
        method: "GET",
        url: `${LOGIN_API}/logout/${token}`,
        headers: getClientSecretBasicAuthHeader(),
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserByTokenQuery,
  useGetUserPlaySettingsQuery,
  useLazyGetUserPlaySettingsQuery,
  useLazyGetUserByTokenQuery,
  usePostUserMetricsMutation,
  useDeleteUserCookieMutation,
  useLogoutMutation,
  useUpdateUserPlaySettingsMutation,
} = extendedApiSlice;
